import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../store/useStore';

export default function SelfHostedSetup() {
  const navigate = useNavigate();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [externalUrls, setExternalUrls] = useState({});
  const [saving, setSaving] = useState({});
  const [saved, setSaved] = useState({});
  const [toast, setToast] = useState('');
  const [toastErr, setToastErr] = useState(false);

  const showToast = (msg, err = false) => {
    setToast(msg); setToastErr(err);
    setTimeout(() => setToast(''), 3000);
  };

  const load = useCallback(async () => {
    try {
      const r = await api.get('/api/links');
      const active = r.data.filter(l => l.is_active);
      setLinks(active);
      const init = {};
      active.forEach(l => { init[l.id] = l.external_url || ''; });
      setExternalUrls(init);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const exportHtml = async (link) => {
    try {
      const resp = await api.get(`/api/links/${link.id}/export`, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `connect-${link.label || link.token?.slice(0, 8) || link.id}.html`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch { showToast('Export failed', true); }
  };

  const saveExternalUrl = async (linkId) => {
    setSaving(s => ({ ...s, [linkId]: true }));
    try {
      await api.patch(`/api/links/${linkId}`, { external_url: externalUrls[linkId] || null });
      setSaved(s => ({ ...s, [linkId]: true }));
      setTimeout(() => setSaved(s => ({ ...s, [linkId]: false })), 2500);
      showToast('Saved');
      load();
    } catch (e) { showToast(e.response?.data?.detail || 'Save failed', true); }
    setSaving(s => ({ ...s, [linkId]: false }));
  };

  return (
    <div>
      <div className="page-header">
        <h1>Self-Hosted Landing Pages</h1>
        <p>Host your connection pages on your own domain.</p>
      </div>

      {/* How it works */}
      <div className="card mb-4" style={{ background: 'rgba(88,166,255,.04)', border: '1px solid rgba(88,166,255,.15)' }}>
        <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 2 }}>
          <strong style={{ color: 'var(--text)', fontSize: 14 }}>How it works</strong>
          <p style={{ marginTop: 8, marginBottom: 0 }}>
            Download the HTML for any of your links, upload it to your web hosting, and set the hosted URL below.
            The sign-in and account linking process is handled by the platform automatically — no additional setup needed.
          </p>
        </div>
      </div>

      {/* Steps */}
      <div className="card mb-4">
        <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', marginBottom: 16 }}>Steps</div>
        <ol style={{ paddingLeft: 20, fontSize: 13, color: 'var(--text2)', lineHeight: 2.4, margin: 0 }}>
          <li>Go to <button className="btn btn-ghost btn-sm" style={{ padding: '0 6px', fontSize: 12 }} onClick={() => navigate('/dashboard/links')}>My Links</button> and click <strong style={{ color: 'var(--text)' }}>Get HTML</strong> on the link you want to host</li>
          <li>Log into cPanel → File Manager → <code>public_html</code> → upload the file</li>
          <li>Note the full URL of the uploaded file (e.g. <code>https://yourdomain.com/connect.html</code>)</li>
          <li>Paste that URL in the field below and save — your dashboard will use it as the shareable link</li>
        </ol>
      </div>

      {/* Per-link external URL */}
      <div className="card">
        <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', marginBottom: 6 }}>Your Links</div>
        <p className="text-sm text-muted" style={{ marginBottom: 16 }}>
          Set the hosted URL for each link. This replaces the default URL shown in the dashboard.
        </p>

        {loading ? (
          <div className="empty-state"><span className="spinner" /></div>
        ) : links.length === 0 ? (
          <div className="empty-state">
            <p className="text-muted text-sm">No active links yet.</p>
            <button className="btn btn-primary btn-sm" style={{ marginTop: 12 }} onClick={() => navigate('/dashboard/links/new')}>
              Create a Link
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {links.map(link => (
              <div key={link.id} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '12px 14px', background: 'var(--surface2)',
                borderRadius: 8, border: '1px solid var(--border)', flexWrap: 'wrap',
              }}>
                <div style={{ flex: '0 0 auto', minWidth: 120 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)' }}>
                    {link.label || <span className="text-muted">Unnamed</span>}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>
                    {link.external_url
                      ? <span style={{ color: 'var(--success)' }}>✓ Hosted</span>
                      : <span>No external URL</span>}
                  </div>
                </div>

                <button className="btn btn-ghost btn-sm" style={{ flexShrink: 0, fontSize: 12 }} onClick={() => exportHtml(link)}>
                  Get HTML
                </button>

                <input
                  className="form-input"
                  style={{ flex: '1 1 240px', fontSize: 13 }}
                  value={externalUrls[link.id] || ''}
                  onChange={e => setExternalUrls(u => ({ ...u, [link.id]: e.target.value }))}
                  placeholder="https://yourdomain.com/connect.html"
                />

                <button
                  className="btn btn-success btn-sm"
                  style={{ flexShrink: 0 }}
                  disabled={saving[link.id]}
                  onClick={() => saveExternalUrl(link.id)}
                >
                  {saving[link.id] ? 'Saving…' : saved[link.id] ? '✓ Saved' : 'Save'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
          background: toastErr ? '#da3633' : '#238636',
          color: '#fff', padding: '10px 18px', borderRadius: 8,
          boxShadow: '0 4px 16px rgba(0,0,0,.4)', fontWeight: 600, fontSize: 13,
        }}>
          {toast}
        </div>
      )}
    </div>
  );
}
